import { TODAY_URGENCY, type TodayItem } from '../shared/contract'
import type { Tone } from '../shared/ui'

/* Spec COG-009 — Admin › Hoje: what the screen derives from GET /admin/hoje (the server already sorts). */

export const URGENCY_TONE: Record<TodayItem['urgency'], Tone> = { atrasado: 'err', hoje: 'warn', em_breve: 'info', info: 'neutral' }
export const URGENCY_LABEL: Record<TodayItem['urgency'], string> = { atrasado: 'Atrasado', hoje: 'Hoje', em_breve: 'Em breve', info: 'Para saber' }

/** Same order as the server: safeguarding cases first, then atrasado, hoje, em_breve, info; then the bigger count. */
export function sortItems(items: readonly TodayItem[]): TodayItem[] {
    const rank = (i: TodayItem) => (/^moderacao-cases/.test(i.id) ? -10 : 0) + TODAY_URGENCY.indexOf(i.urgency)
    return [...items].sort((a, b) => rank(a) - rank(b) || b.count - a.count)
}

/** "/staff/fichas?q=FD-1" -> { to: "/staff/fichas", search: { q: "FD-1" } } for the router Link. */
export function splitPath(path: string): { to: string; search: Record<string, string> } {
    const at = path.indexOf('?')
    if (at < 0) return { to: path, search: {} }
    return { to: path.slice(0, at), search: Object.fromEntries(new URLSearchParams(path.slice(at + 1))) }
}

/** "3 coisas pedem atenção · 1 atrasada" for the header line. */
export function summaryLine(items: readonly TodayItem[]): string {
    if (items.length === 0) return 'Nada pendente nas suas áreas.'
    const late = items.filter(i => i.urgency === 'atrasado').length
    const head = `${items.length} ${items.length === 1 ? 'coisa pede' : 'coisas pedem'} atenção`
    return late ? `${head} · ${late} ${late === 1 ? 'atrasada' : 'atrasadas'}` : head
}

/** The one sentence of the calm header: "3 coisas pedem sua atenção, 1 atrasada." */
export function todayTitle(items: readonly TodayItem[]): string {
    if (items.length === 0) return 'Tudo em dia nas suas áreas.'
    const late = items.filter(i => i.urgency === 'atrasado').length
    const head = `${items.length} ${items.length === 1 ? 'coisa pede' : 'coisas pedem'} sua atenção`
    return late ? `${head}, ${late} ${late === 1 ? 'atrasada' : 'atrasadas'}.` : `${head}.`
}

export type UrgencyFilter = TodayItem['urgency'] | 'all'

/** Segment chips: "Tudo" + each urgency that has items (the chosen one stays even when it empties). */
export function urgencyOptions(items: readonly TodayItem[], current: UrgencyFilter): { value: UrgencyFilter; label: string; count: number }[] {
    const levels = TODAY_URGENCY.map(u => ({ value: u as UrgencyFilter, label: URGENCY_LABEL[u], count: items.filter(i => i.urgency === u).length }))
    return [{ value: 'all', label: 'Tudo', count: items.length }, ...levels.filter(o => o.count > 0 || o.value === current)]
}

export const byUrgency = (items: readonly TodayItem[], u: UrgencyFilter) => (u === 'all' ? [...items] : items.filter(i => i.urgency === u))

/** Lista e detalhe (27/10): the short number-title of the header ("4 pendências hoje"). */
export function todayHeadline(items: readonly TodayItem[]): string {
    if (items.length === 0) return 'Tudo em dia'
    return `${items.length} ${items.length === 1 ? 'pendência' : 'pendências'} hoje`
}

/** Up to 2 supporting numbers: the late ones (only when some) and the areas already clear (only when some). */
export function todayFacts(today: { items: readonly TodayItem[]; clear: readonly unknown[] }): { value: number; label: string }[] {
    const late = today.items.filter(i => i.urgency === 'atrasado').length
    return [
        ...(late ? [{ value: late, label: late === 1 ? 'atrasada' : 'atrasadas' }] : []),
        ...(today.clear.length ? [{ value: today.clear.length, label: today.clear.length === 1 ? 'área em dia' : 'áreas em dia' }] : []),
    ]
}
